/* eslint-disable */
// @ts-nocheck
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NextLink from 'next/link';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import QuickView from 'components/commercetools-ui/organisms/product/product-quick-view';
import Prices from 'components/commercetools-ui/organisms/product/product-tile/prices';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import useTrack from './useTrack';
import { ShareIcon } from '@heroicons/react/24/outline';
import { BIM_FILE_TYPES, BRAND_FACET_ATTRIBUTE,INSTALLATION_GUIDELINES, INSTALLATION_GUIDELINES_SET, ITEM_CODE, ITEM_NAME, TECH_IMAGE,  TECH_MANUAL,TECH_MANUAL_SET } from 'helpers/constants/attributes';
import ColorSwatches from './swatches';
import usePath from 'helpers/hooks/usePath';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import Button from 'components/commercetools-ui/atoms/button';
// import ShopOnline from '../shop-online';
import { localeMapping } from 'helpers/constants/localeMapping';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { categories } from 'helpers/mocks/mockData';
import Search from 'components/commercetools-ui/atoms/search-algolia';
import useTranslation from 'providers/i18n/hooks/useTranslation';
import JSZip from 'jszip';
import { useFormat } from 'helpers/hooks/useFormat';
import { saveAs } from 'file-saver';
import Spinner from '../../spinner';
import { BIM_PLP_FILE_DOWNLOADED, BIM_LIB_PRODUCT_ALL_FILE_DOWNLOADED } from 'helpers/constants/events';

interface ProductTileProps {
  product: Product;
  onClick?: () => void;
  isSearchResult?: boolean;
  isCollectionPage?: boolean;
  isCategoryPage?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  isBimLibraryPage?: any;
  disableVariants?: boolean;
  images?: string[];
  selectedVariantIndex?: number;
  onAddToProject?: () => void;
  handleSelectProduct?: (product: Product) => void;
  getIsCheckedProduct?: (product: Product) => boolean;
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  images,
  onClick,
  isSearchResult = false,
  isCollectionPage = false,
  isCategoryPage = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  selectedVariantIndex = 0,
  isBimLibraryPage,
  onAddToProject,
  handleSelectProduct,
  getIsCheckedProduct,
}) => {
  const { locale, query } = useParams();
  const router = useRouter();
  const localeKey = localeMapping[locale];
  const [isDesktopSize] = useMediaQuery(desktop);
  const { ref } = useTrack({ product });
  const { translate } = useTranslation();

  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;
  const [selectedVariant, setSelectedVariant] = useState(
    () => product?.variants?.filter((variant) => variant.id === product?.masterVariantID)?.[0] || product.variants?.[0],
  );
  const width = useWindowWidth();
  const hasDiscount =
    selectedVariant?.discountedPrice?.centAmount &&
    selectedVariant?.price?.centAmount &&
    selectedVariant?.discountedPrice?.centAmount < selectedVariant?.price?.centAmount;

  const discountedPrice = selectedVariant?.discountedPrice;
  const { path, absolutePath } = usePath();
  const discountPercentage = selectedVariant
    ? (((selectedVariant.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
        (selectedVariant.price?.centAmount as number)) *
      100
    : 0;

  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const { formatMessage } = useFormat({ name: 'product' });
  const [spinner, setSpinner] = useState(false);
  const searchParams = useSearchParams();
  const categoryLink = searchParams.get('category') as string;
  const collectionLink = searchParams.get('collection') as string;
  const buttonIsVisible = useMemo(
    () => (imageHovered || buttonHovered) && isDesktopSize,
    [imageHovered, buttonHovered, isDesktopSize],
  );
  const hideButton = () => {
    setImageHovered(false);
    setButtonHovered(false);
  };
  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);
  const bimFileTypes = product?.variants?.[0]?.attributes?.[BIM_FILE_TYPES];
  const filteredBimFileTypes = bimFileTypes?.filter(item => item.toLowerCase() !== 'bom');

  const productUrl = useMemo(() => {
    if (!product._url) return getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour);

    return (
      product._url +
      (isSearchResult ? '?sr=1' : '') +
      (!isBimLibraryPage && isCategoryPage ? '?category=' + path : '') +
      (isCollectionPage ? '?collection=' + path : '') +
      (!isBimLibraryPage && !isSearchResult && !isCategoryPage && categoryLink ? '?category=' + categoryLink : '') +
      (isBimLibraryPage ? '?fromBim=true' : '')
    );
  }, [product, selectedVariant, isSearchResult]);

  const getFilenameFromUrl = (url: string) => {
    const parts = url.split('/');
    return parts[parts.length - 1];
  };
  const fetchAndZipFile = async (url: string, index: number, locale: string, zip: any) => {
    try {
      const uniqueUrl = `${url.replaceAll('ProductThumbnail', 'Original')}?cacheBust=${Date.now()}_${index}`;
      const response = await fetch(uniqueUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file from URL: ${url}`);
      }
  
      const content = await response.blob();
      const filename = getFilenameFromUrl(url);
      zip.file(filename, content); 
    } catch (error) {
      console.error(`Error fetching file from ${url}:`, error);
      throw error; 
    }
  };
  
  const downloadAllFiles = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    const failedFiles: string[] = [];
    const urls: string[] = [];
    const attributes = product?.variants?.[0]?.attributes || {};
    const fileTypes = attributes[BIM_FILE_TYPES] || [];
  
    try {
      setSpinner(true);
  
      fileTypes.forEach((fileType) => {
        const fileUrl = attributes[`${fileType}-file`];
        if (fileUrl) urls.push(fileUrl);
      });
  
      const addUrlsFromSet = (set: string[]) => set?.length && urls.push(...set);
      if (attributes[TECH_MANUAL_SET]?.length) addUrlsFromSet(attributes[TECH_MANUAL_SET]);
      else if (attributes[TECH_MANUAL]) urls.push(attributes[TECH_MANUAL]);
      if (attributes[INSTALLATION_GUIDELINES_SET]?.length) addUrlsFromSet(attributes[INSTALLATION_GUIDELINES_SET]);
      else if (attributes[INSTALLATION_GUIDELINES]) urls.push(attributes[INSTALLATION_GUIDELINES]);
      if (attributes[TECH_IMAGE]) urls.push(attributes[TECH_IMAGE]);
  
      const zip = new JSZip();
  
      await Promise.all(
        urls.map((url, index) =>
          fetchAndZipFile(url, index, locale, zip).catch(() => {
            failedFiles.push(url);
          })
        )
      );
  
      if (urls.length > failedFiles.length) {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'files.zip');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: BIM_LIB_PRODUCT_ALL_FILE_DOWNLOADED,
          'product sku': product?.variants[0]?.sku,
        });
      }
  
      if (failedFiles.length > 0) {
        setErrorMessage(
          `Some files are currently unavailable for download. Please try again later.`
        );
        console.error('Failed files:', failedFiles);
      }
    } catch (error) {
      console.error('Error creating ZIP file:', error);
      setErrorMessage('An error occurred while creating the ZIP file. Please try again later.');
    } finally {
      setSpinner(false);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };
  
  const downloadFile = async (fileUrl: string, fileType: string) => {
    try {
      setSpinner(true);
  
      const uniqueUrl = fileUrl.replaceAll('ProductThumbnail', 'Original');
      const filename = uniqueUrl.split('/').pop()?.split('?')[0] || 'download';
  
      const link = document.createElement('a');
      link.href = uniqueUrl;
      link.download = filename; 
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: BIM_PLP_FILE_DOWNLOADED,
        'product': product?.variants[0]?.sku,
        'file type':fileType
      });
      setSpinner(false);
    } catch (error) {
      console.error('Error downloading the file:', error);
      setErrorMessage('Failed to download the file. Please try again later.');
      setSpinner(false);
    } finally {
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  return (
    <div className={`${isBimLibraryPage ? 'h-full' : ''}`}>
      <div onClick={onClick} ref={ref} className={`relative ${isBimLibraryPage ? 'h-full' : ''}`}>
        {isBimLibraryPage && (
          <div className="absolute right-[18px] top-[14px] z-10">
            <div
              className="relative h-24 w-44 cursor-pointer rounded-full shadow-sm"
              onClick={(e) => handleSelectProduct(product)}
            >
              <input
                type="checkbox"
                id={product?.variants[0]?.sku}
                defaultChecked={getIsCheckedProduct && getIsCheckedProduct(product)}
                className="bim-toggle absolute m-[2px] h-20 w-20 cursor-pointer appearance-none rounded-full shadow-md focus:outline-none"
              />
              <label
                htmlFor={product?.variants[0]?.sku}
                className="toggle-label block h-24 w-44 cursor-pointer overflow-hidden rounded-full bg-[#EEEEEE]"
              />
            </div>
          </div>
        )}
        <NextLink href={`/${locale}${productUrl}`} passHref prefetch={false}>
          <div
            className={`cursor-pointer ${isBimLibraryPage ? 'flex h-full flex-col' : ''}`}
          >
            <div className="relative">
              <div
                className="relative w-full"
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
              >
                <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem]" style={{ paddingBottom: '100%' }}>
                  <Image
                    priority
                    src={
                      selectedVariant?.images?.[0]
                        ? encodeURI(selectedVariant?.images?.[0].toString())
                        : '/images/no-image-placeholder/product-thumbnail.jpg'
                    }
                    alt={product.name ?? ''}
                    style={{ objectFit: 'contain', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 "
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    onError={(event: any) => {
                      event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                      event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className={` ${imageHovered ? 'bg-[#F3EDE9]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]'} ${isBimLibraryPage ? 'flex-1 pt-16' : 'height-13rem height-14rem-2xl pt-12'
                } px-12 hover:bg-[#F3EDE9] md:px-24 lg:px-24 xl:px-24 2xl:px-24`}
            >
              <div
                className={`${isBimLibraryPage
                    ? 'flex h-full flex-col justify-between'
                    : 'flex h-[8rem]  flex-col  justify-between 2xl:h-[9rem] '
                  }`}
              >
                {!isBimLibraryPage && (
                  <div className="flex items-center justify-between">
                    <span className="text-10 font-normal leading-6 text-[#333] md:text-12 2xl:text-14">
                      {product?.categories &&
                        (product?.categories[0]?.name ||
                          product?.categories?.[1]?.categoryId ||
                          product?.categories[0]?.categoryId)}
                    </span>
                  </div>
                )}
                <div className="block max-w-[100%] ">
                  <p
                    className={`${isBimLibraryPage
                        ? 'mb-16 text-[#333333] md:leading-[26px]'
                        : 'text-[#2B2C39] md:leading-[1.625rem] lg:text-primary-black'
                      } line-clamp-2 text-14 font-medium capitalize md:text-16 2xl:text-18`}
                  >
                    {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                  </p>
                </div>
                {isBimLibraryPage && (
                  <div className="flex flex-col justify-between">
                    <div>
                      <span className="pt-16 text-14 font-normal text-[#333333]">{translate(`product.bim.files`)}</span>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-8 py-14 sm:pb-20 sm:pt-16 md:gap-y-16 min-h-[60px]">
                        {(filteredBimFileTypes ?? [])?.map((bf, idx) => (
                          <button
                            key={bf}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const fileUrl = product?.variants?.[0]?.attributes?.[`${bf}-file`];
                              downloadFile(fileUrl, bf);
                            }}
                            className={`${idx === (filteredBimFileTypes ?? [])?.length - 1
                                ? ''
                                : 'border-r border-[#666666] pr-8'
                              } inline cursor-pointer text-14 font-medium text-[#666666] underline sm:text-16`}
                          >
                            {bf}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={downloadAllFiles}
                        disabled={filteredBimFileTypes?.length === 0}
                        className="cursor-pointer bottom-[24px] pb-24 text-left text-16 font-medium text-[#666666] underline disabled:pointer-events-none disabled:opacity-50"
                      >
                        {translate(`product.download.files`)}
                      </button>
                      {errorMessage && (
                        <div className="mt-[-20px] pb-[8px] text-14 font-medium text-red-500">{errorMessage}</div>
                      )}
                    </div>
                  </div>
                )}

                {!isBimLibraryPage && (
                  <div
                    className={`mt-2 flex flex-col gap-2 text-10 font-medium text-[#090909] md:mt-4 md:flex-row md:items-center md:justify-between md:gap-0 md:text-14 lg:gap-0 xl:gap-0 2xl:gap-0 2xl:text-16`}
                  >
                    <span>
                      <span className="text-[#666]">Product code</span>{' '}
                      {product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}
                    </span>
                    <span>
                      {product?.variants && (
                        <ColorSwatches
                          product={product}
                          variants={product?.variants}
                          setSelectedVariant={setSelectedVariant}
                        />
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </NextLink>
        {!isBimLibraryPage && (
          <button
            type="button"
            onClick={() => onAddToProject && onAddToProject()}
            className="absolute bottom-26-sm bottom-20-2xl left-12-sm left-24-md padding-x-17 padding-y-9 font-size12-sm fontSize14-2xl bg-black margin-t-8 cursor-pointer text-white shadow-sm"
          >
            Add to Project
          </button>
        )}
        {spinner && <Spinner />}
      </div>
    </div>
  );
};

export default ProductTile;
